import React from 'react';
import { ArrowRight } from 'lucide-react';

/** SectionHero – Brand-led homepage with product details, CTAs, and stats. */
export default function SectionHero({ onNavigate, content }) {
  const hero = content?.hero || {};
  const brand = hero.brand ?? 'JungleBrick';
  const headlineLine1 = hero.headlineLine1 ?? 'PromptLens';
  const headlineLine2 = hero.headlineLine2 ?? '& OmniLens';
  const tagline = hero.tagline ?? 'Control AI spend. Unify cloud cost.';
  const subtext = hero.subtext ?? '';
  const ctaPrimary = hero.ctaPrimary ?? 'Explore Products';
  const ctaPrimarySuffix = hero.ctaPrimarySuffix ?? '→';
  const ctaSecondary = hero.ctaSecondary ?? 'View Our Work';
  const stats = content?.stats?.items ?? [];
  const capabilities = Array.isArray(hero.capabilities) ? hero.capabilities : [];
  const heroBgUrl = hero.imageUrl || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80';

  return (
    <section className="relative min-h-[90vh] flex items-center pt-24 pb-16 px-4 sm:px-6 md:px-8 lg:px-12 overflow-hidden">
      <div className="absolute inset-0 bg-forest-bg" aria-hidden="true" />
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${heroBgUrl})`,
          opacity: 'var(--jb-hero-image-opacity)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{ background: 'var(--jb-hero-overlay)' }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-100"
        style={{
          background: 'radial-gradient(ellipse 70% 60% at 65% 40%, var(--jb-glow-cyan) 0%, transparent 70%), radial-gradient(ellipse 40% 50% at 20% 70%, var(--jb-glow-green) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 lg:gap-16 items-center">
        <div>
          <p
            className="font-mono text-[0.7rem] text-cyan uppercase tracking-[0.15em] mb-5"
          >
            {brand}
          </p>

          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight mb-5" style={{ letterSpacing: '-0.02em' }}>
            <span className="text-cyan">{headlineLine1}</span>
            <br />
            <span className="text-text-hero">{headlineLine2}</span>
          </h1>

          {tagline && (
            <p className="font-display font-semibold text-xl sm:text-2xl text-accent-green mb-6 leading-snug">
              {tagline}
            </p>
          )}

          {subtext && (
            <p className="font-body text-lg text-text-body max-w-[480px] mb-6 leading-[1.75]">
              {subtext}
            </p>
          )}

          {capabilities.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {capabilities.map((cap, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-3 py-1 rounded-full text-[0.72rem] font-mono font-medium border"
                  style={{
                    background: i % 2 === 0 ? 'var(--jb-cyan-dim)' : 'var(--jb-accent-green-dim)',
                    borderColor: 'var(--jb-border)',
                    color: i % 2 === 0 ? 'var(--jb-cyan)' : 'var(--jb-accent-green)',
                  }}
                >
                  {cap}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {ctaPrimary && (
              <button
                type="button"
                onClick={() => onNavigate('products')}
                className="jb-cta inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-base shadow-lg hover:opacity-90 hover:shadow-cyan/30 transition"
                style={{ boxShadow: '0 4px 24px rgba(0,229,204,0.25)' }}
              >
                {ctaPrimary} {ctaPrimarySuffix}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {ctaSecondary && (
              <button
                type="button"
                onClick={() => { window.location.hash = '#work'; }}
                className="px-8 py-3.5 rounded-xl border border-white/20 text-text-body font-body font-semibold hover:border-cyan hover:text-cyan transition"
              >
                {ctaSecondary}
              </button>
            )}
          </div>
        </div>

        {stats.length > 0 && (
          <div className="hidden md:flex flex-col gap-4">
            {stats.slice(0, 4).map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-5 p-5 sm:p-6 rounded-[14px] border bg-forest-card hover:border-cyan/50 transition"
                style={{ borderColor: 'var(--jb-border)' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                  style={{ background: i % 2 === 0 ? 'var(--jb-cyan-dim)' : 'var(--jb-accent-green-dim)' }}
                >
                  {['☁️', '📉', '⚡', '🛡️'][i] || '•'}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-xl sm:text-[1.6rem] text-text-hero leading-none">
                    {item.value ?? ''}
                  </div>
                  <div className="font-body text-[0.85rem] text-text-sub mt-1.5 font-normal leading-snug">
                    {item.label ?? ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
